import React, { useState, useEffect, useRef } from 'react';

import { isElementOfType } from '../../utilities/components';

import { useMergeRefs } from '../../hooks/useMergeRefs';

import WpDropdownToggle from './WpDropdownToggle';
import WpDropdownMenu from './WpDropdownMenu';
import { usePopover, Placement } from '../popover';

export interface Props {
  isOpen?: boolean;
  setOpen?: Function;
  children?: React.ReactNode;
  disabled?: boolean;
  withBoxShadow?: boolean;
  withMaxHeight?: boolean;
  placement?: Placement;
  onClickCallback?: (isOpen: boolean) => void;
}

const WpDropdown: React.FC<Props> = ({
  isOpen,
  setOpen,
  children,
  disabled,
  withMaxHeight = true,
  withBoxShadow = false,
  placement = 'bottom-start',
  onClickCallback = null,
}: Props) => {
  const toggleRef = useRef(null);
  const [_isOpen, _setIsOpen] = useState<boolean>(false);

  const handleSetIsOpen = value => {
    if (onClickCallback) {
      onClickCallback(value);
    }
    if (!disabled) {
      if (setOpen) {
        setOpen(value);
      }
      _setIsOpen(value);
    }
  };
  const popover = usePopover({
    event: 'click',
    open: _isOpen,
    onOpenChange: handleSetIsOpen,
    placement,
    showArrow: false,
    withPadding: false,
    withBoxShadow: withBoxShadow,
  });

  const ref = useMergeRefs([toggleRef, popover.targetRef]);

  const toggleIsOpen = () => handleSetIsOpen(!_isOpen);

  useEffect(() => {
    handleSetIsOpen(isOpen);
  }, [isOpen]);

  const childrenLength = React.Children.count(children);
  const isFirstChildDropdownToggle = children && childrenLength > 1 && isElementOfType(children[0], WpDropdownToggle);
  const isSecondDropdownMenu = children && childrenLength > 1 && isElementOfType(children[1], WpDropdownMenu);

  const isValidChildren = isFirstChildDropdownToggle && isSecondDropdownMenu;

  if (!isValidChildren) return null;

  return (
    <>
      {React.Children.map(children, (child: React.ReactElement<any>) => {
        if (child && isElementOfType(child, WpDropdownToggle)) {
          return React.cloneElement(child, { onClick: toggleIsOpen, ref, disabled });
        } else if (child && isElementOfType(child, WpDropdownMenu)) {
          return React.cloneElement(child, {
            isOpen: _isOpen,
            toggle: toggleIsOpen,
            popoverContext: popover,
            disabled,
            placement,
            target: toggleRef,
            withMaxHeight: withMaxHeight,
          });
        }
        return child;
      })}
    </>
  );
};

export default WpDropdown;
